import { useCallback, useMemo } from 'react'
import { PhoneNumber } from 'libphonenumber-js'
import { FlexColumn, FlexRow } from '@/atoms/FlexContainers'
import { MusicIcon } from '@/atoms/Icons/MusicIcon'
import { CaptionMD } from '@/atoms/Text'
import { ENGLISH_SPEAKING_REGIONS } from '@/constants/regions'
import { TheatricalShowtime } from '@/types/codegen-federation'
import { useLocale } from '@/utils/LocaleUtil'
import { useSafeAnalytics } from '@/utils/analytics'
import { useSafeRouter } from '@/utils/nextUtils/safe-router'
import { useTranslate } from '@/utils/translate/translate-client'
import EmptyShowtimesState from '@/views/TicketCheckoutViews/ShowtimesView/components/EmptyShowtimesState'
import GroupTicketsButton from '@/views/TicketCheckoutViews/ShowtimesView/components/GroupTicketsButton'
import { ShowTimeLink } from '@/views/TicketCheckoutViews/ShowtimesView/components/ShowTimeLink/ShowTimeLink'
import { GroupTicketsModalTypes } from '@/views/TicketCheckoutViews/ShowtimesView/components/modals/GroupTicketsModal/GroupTicketsModal'
import { getLanguageNameUsingUserLocale } from '@/views/TicketCheckoutViews/utils'
import { useTheatricalPromotionContext } from '../../components/TheatricalPromotionContext'
import { ShowtimesVenue } from '../hooks/useVenueSearch/useVenueSearch'
import { isShowtimePast } from '../utils/utils'
import { CheckSeatAvailabilityButton } from './SeatAvailability/CheckSeatAvailabilityButton'
import { TicketsAnalyticsPayload, useTicketsContext } from './TicketsContext'

interface ShowtimesListProps {
  showTimes: TheatricalShowtime[]
  venueId: string
  onClick: (showTime: TheatricalShowtime) => void
  shouldShowGroupTicketsButton: boolean
  openGroupTicketsModal?: (type: GroupTicketsModalTypes) => void
  venuePhoneNumber: PhoneNumber | undefined
  className?: string
  theaterSlug: string
  countryCode: string
  isDisabled: boolean
  venue: ShowtimesVenue
  analyticsPayload: TicketsAnalyticsPayload
  showSeatAvailabilityButton: boolean
  handleToggleSeatAvailability: () => void
  shouldShowNewBadge?: boolean
}

const ENGLISH_SPEAKING_COUNTRY_CODES = new Set(ENGLISH_SPEAKING_REGIONS)

const ShowtimesList = ({
  showTimes,
  venueId,
  onClick,
  venue,
  shouldShowGroupTicketsButton,
  openGroupTicketsModal,
  analyticsPayload,
  theaterSlug,
  countryCode,
  className,
  venuePhoneNumber,
  isDisabled,
  showSeatAvailabilityButton,
  handleToggleSeatAvailability,
  shouldShowNewBadge = false,
}: ShowtimesListProps) => {
  const { query } = useSafeRouter()
  const { t } = useTranslate('tickets')
  const { track } = useSafeAnalytics()
  const { locale } = useLocale()
  const { experiments } = useTicketsContext()
  const isSeatAvailabilityEnabled = !!experiments?.['showtime_seat_availability_v3']
  const isPhilippinesFreeTicketFlow = !!query?.promo && countryCode === 'PH'
  const { promo } = useTheatricalPromotionContext()

  const getShowtimeReservableName = useCallback(
    (seatsReservable: boolean | null | undefined) => {
      if (seatsReservable == null) return null
      if (seatsReservable) return t('reservedSeating', 'Reserved Seating')
      return t('generalAdmission', 'General Admission')
    },
    [t],
  )

  const getSubtitleName = useCallback(
    (subtitleLanguage: string | null | undefined) => {
      if (subtitleLanguage == null) return null
      if (subtitleLanguage === locale) return t('closedCaptions', 'CC')
      return `${getLanguageNameUsingUserLocale(locale, subtitleLanguage)} ${t('ticketSubtitles', 'Subtitles')}`
    },
    [locale, t],
  )

  const getAudioLanguageName = useCallback(
    (audioLanguage: string | null | undefined) => {
      if (audioLanguage == null) return null
      if (ENGLISH_SPEAKING_COUNTRY_CODES.has(countryCode) && audioLanguage === 'en') return null
      return `${getLanguageNameUsingUserLocale(locale, audioLanguage)} ${t('ticketSpoken', 'Spoken')}`
    },
    [locale, countryCode, t],
  )

  const handleGroupTicketClick = useCallback(() => {
    track('Clicked Group Tickets Available')
    openGroupTicketsModal && openGroupTicketsModal('tickets')
  }, [openGroupTicketsModal, track])

  const organizedShowtimes = useMemo(() => {
    return organizeShowtimes(showTimes)
  }, [showTimes])

  if (showTimes?.length === 0 && !isPhilippinesFreeTicketFlow) {
    return (
      <EmptyShowtimesState
        venuePhoneNumber={venuePhoneNumber}
        theatricalSlug={theaterSlug}
        venue={venue}
        analyticsPayload={analyticsPayload}
      />
    )
  }

  return (
    <FlexColumn className={className}>
      {organizedShowtimes?.map((organizedList, index) => {
        const reservableName = getShowtimeReservableName(organizedList[0]?.reservable)
        const subtitleName = getSubtitleName(organizedList[0]?.subtitleLanguage)
        const audioName = getAudioLanguageName(organizedList[0]?.language)

        return (
          <FlexColumn className="gap-3" key={`${venueId}-${index}`}>
            <FlexRow className="flex-wrap gap-2">
              {!!subtitleName && (
                <CaptionMD className="capitalize" color="core-gray-600">
                  {subtitleName}
                </CaptionMD>
              )}
              {!!audioName && (
                <>
                  {!!subtitleName && <div className="h-1 w-1 rounded-full bg-core-gray-600" />}
                  <CaptionMD className="capitalize" color="core-gray-600">
                    {audioName}
                  </CaptionMD>
                </>
              )}
              {reservableName && (
                <>
                  {(!!subtitleName || !!audioName) && <div className="h-1 w-1 rounded-full bg-core-gray-600" />}
                  <CaptionMD color="core-gray-600">{reservableName}</CaptionMD>
                </>
              )}
              {organizedList.some((showTime) => showTime.releaseType === 'SING_ALONG') && (
                <>
                  {(!!subtitleName || !!audioName || !!reservableName) && (
                    <div className="h-1 w-1 rounded-full bg-core-gray-600" />
                  )}
                  <FlexRow className="gap-0.5">
                    <MusicIcon
                      color={promo?.code === 'SINGALONG' ? 'core-gray-950' : 'core-gray-600'}
                      size={16}
                      className="shrink-0"
                    />
                    <CaptionMD color={promo?.code === 'SINGALONG' ? 'core-gray-950' : 'core-gray-600'}>
                      {t('singAlongVersion', 'Sing-Along Version')}
                    </CaptionMD>
                  </FlexRow>
                </>
              )}
            </FlexRow>

            <div className="auto flex flex-wrap justify-items-start gap-2">
              {organizedList.map((showTime, index) => {
                if (!showTime.utcStartTime) return null
                return (
                  <ShowTimeLink
                    countryCode={countryCode}
                    key={`${venueId}-${showTime.utcStartTime}-${index}`}
                    showTime={showTime}
                    onClick={() => onClick(showTime)}
                    isDisabled={isDisabled}
                    shouldShowNewBadge={shouldShowNewBadge}
                  />
                )
              })}
              {shouldShowGroupTicketsButton && !isSeatAvailabilityEnabled && (
                <GroupTicketsButton onClick={handleGroupTicketClick} />
              )}
              {showSeatAvailabilityButton &&
                organizedList.some((showTime) => showTime.reservable && !isShowtimePast(showTime)) && (
                  <CheckSeatAvailabilityButton
                    buttonText={t('viewSeats', 'View Seats')}
                    onClick={handleToggleSeatAvailability}
                  />
                )}
            </div>
          </FlexColumn>
        )
      })}
    </FlexColumn>
  )
}

export default ShowtimesList

const getShowtimeReservability = (showtime: TheatricalShowtime) => {
  if (showtime?.reservable == null) return 'unknown'
  if (showtime.reservable) return 'reservable'
  return 'nonReservable'
}

const organizeShowtimes = (showtimes: TheatricalShowtime[]): TheatricalShowtime[][] => {
  const organizedByLanguage = showtimes.reduce((acc, showtime) => {
    const seatsReservable = getShowtimeReservability(showtime)
    const subtitleLanguage = showtime?.subtitleLanguage || 'noSubtitle'
    const language = showtime?.language || 'noLanguage'
    const tempKey = `${seatsReservable}${subtitleLanguage}${language}`

    return { ...acc, [tempKey]: [...(acc[tempKey] ?? []), showtime] }
  }, {} as Record<string, TheatricalShowtime[]>)

  return sortShowtimesByLanguage(Object.values(organizedByLanguage))
}

const sortShowtimesByLanguage = (showtimes: TheatricalShowtime[][]) => {
  const sortedShowtimesBySubtitle = sortBySubtitleLanguage(showtimes)
  return sortByAudioLanguage(sortedShowtimesBySubtitle)
}

const sortBySubtitleLanguage = (showtimes: TheatricalShowtime[][]) => {
  return showtimes.sort((a, b) => {
    if (a.some((showtime) => showtime.subtitleLanguage == null)) return -1
    if (b.some((showtime) => showtime.subtitleLanguage == null)) return 1
    if (a.some((showtime) => showtime.subtitleLanguage == 'en')) return -1
    if (b.some((showtime) => showtime.subtitleLanguage == 'en')) return 1
    return 0
  })
}

const sortByAudioLanguage = (showtimes: TheatricalShowtime[][]) => {
  return showtimes.sort((a, b) => {
    if (a.some((showtime) => showtime.language == 'en' && showtime.subtitleLanguage == null)) return -1
    if (b.some((showtime) => showtime.language == 'en' && showtime.subtitleLanguage == null)) return 1
    return 0
  })
}
